import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Button, Form, Image, Modal } from 'react-bootstrap';
import axios from 'axios';
import { createApiUrl } from '../utils/apiConfig';
import { assetFileDisplayName } from '../utils/assetDisplayName';
import { restrictedVideoProps } from '../utils/restrictedVideo';
import './AllPlans.css';

const AllPlans = ({ plans, assets, refetchData }) => {
  const navigate = useNavigate();
  const [selectedAssets, setSelectedAssets] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createPlanData, setCreatePlanData] = useState({
    name: '',
    duration: '',
    price: '',
    description: '',
    type: 'subscription',
    workshopDate: ''
  });
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    planId: null,
    planName: ''
  });

  const handleAssetSelection = (planId, e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setSelectedAssets(prev => ({ ...prev, [planId]: selectedOptions }));
  };

  const handleAddAssets = async (planId) => {
    try {
      const response = await axios.post(createApiUrl(`/api/add-assets-to-plan/${planId}`), { 
        assetIds: selectedAssets[planId],
        planId: planId
      }, { withCredentials: true });
      
      if (response.data.success) {
        alert('Assets added to plan successfully!');
        refetchData();
        // Clear the selection after adding
        setSelectedAssets(prev => ({ ...prev, [planId]: [] }));
      } else {
        alert('Failed to add assets to plan');
      }
    } catch (error) {
      console.error('Error adding assets to plan:', error);
      alert('Error adding assets to plan: ' + (error.response?.data?.message || error.message));
    }
  };
  
  const handleRemoveAsset = async (planId, assetId) => {
    if(window.confirm('Are you sure you want to remove this asset?')) {
        try {
            const response = await axios.post(createApiUrl(`/api/remove-asset-from-plan/${assetId}`), { planId }, { withCredentials: true });
            if (response.data.success) {
                alert('Asset removed from plan successfully!');
                refetchData();
            } else {
                alert('Failed to remove asset from plan');
            }
        } catch (error) {
            console.error('Error removing asset from plan:', error);
            alert('Error removing asset from plan: ' + (error.response?.data?.message || error.message));
        }
    }
  };
  
  const handleDeletePlan = (planId, planName) => {
    setDeleteModal({
      show: true,
      planId: planId,
      planName: planName
    });
  };

  const confirmDeletePlan = async () => {
    try {
      await axios.post(createApiUrl(`/api/delete-plan/${deleteModal.planId}`), {}, { withCredentials: true });
      setDeleteModal({ show: false, planId: null, planName: '' });
      refetchData();
    } catch(error) {
      console.error('Error deleting plan', error);
      setDeleteModal({ show: false, planId: null, planName: '' });
    }
  };

  const cancelDeletePlan = () => {
    setDeleteModal({ show: false, planId: null, planName: '' });
  };
  
  const confirmDeleteAsset = async (assetId) => {
    if(window.confirm('Are you sure you want to delete this asset permanently?')) {
        try {
            await axios.delete(createApiUrl(`/api/asset/${assetId}`), { withCredentials: true });
            refetchData();
        } catch (error) {
            console.error('Error deleting asset:', error);
        }
    }
  }

  const handleCreatePlan = async (e) => {
    e.preventDefault();
    try {
      await axios.post(createApiUrl('/api/create-plan'), createPlanData, { withCredentials: true });
      setShowCreateModal(false);
      setCreatePlanData({
        name: '',
        duration: '',
        price: '',
        description: '',
        type: 'subscription',
        workshopDate: ''
      });
      refetchData();
    } catch (error) {
      console.error('Error creating plan:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCreatePlanData(prev => ({ ...prev, [name]: value }));
  };

  const formatDuration = (duration) => {
    if (duration < 30) return `${duration} days`;
    if (duration % 365 === 0) return `${duration / 365} year${duration / 365 > 1 ? 's' : ''}`;
    return `${Math.ceil(duration / 30)} months`;
  };

  return (
    <div className="all-plans-container">
      <div className="text-center mb-4">
        <Button variant="primary" onClick={() => setShowCreateModal(true)}>
          Create Plan
        </Button>
      </div>
      <div className="mb-3"></div>
      <Row>
        {plans.map(plan => (
          <Col key={plan._id} md={4}>
            <Card>
              <Card.Body>
                <div className="card-content">
                  <h3>{plan.name}</h3>
                  
                  {plan.type === 'workshop' && (
                    <div>
                      <label htmlFor={`workshopDate${plan._id}`}>Select Date:</label>
                      <input type="date" id={`workshopDate${plan._id}`} name="workshopDate" className="form-control" />
                      <Button variant="secondary" size="sm" className="mt-2">Set Date</Button>
                    </div>
                  )}
                  
                  <p>Duration: {formatDuration(plan.duration)}</p>
                  <p>Price: £{plan.price}</p>
                  <p className="preserve-whitespace">Description: {plan.description}</p>
                  
                  {plan.type !== 'workshop' && <h4>Lessons:</h4>}

                  {plan.assets && plan.assets.length > 0 && plan.assets.map(asset => (
                    <div key={asset._id}>
                      <h5>{asset.name}</h5>
                      <p className="preserve-whitespace asset-description">{asset.description}</p>
                      
                      {asset.video?.thumbnail && (
                        <Image 
                          src={asset.video.thumbnail} 
                          alt={`Thumbnail for ${asset.name}`} 
                          className="asset-thumbnail thumbnail" 
                          style={{cursor: 'pointer', display: 'block'}}
                          onClick={(e) => {
                            const video = e.target.nextElementSibling;
                            e.target.style.display = 'none';
                            video.style.display = 'block';
                            video.play();
                          }}
                        />
                      )}
                      
                      {asset.file?.url && (
                        <video
                          controls
                          {...restrictedVideoProps}
                          height="200"
                          width="280"
                          className="hidden asset-video"
                          style={{ display: 'none' }}
                          onEnded={(e) => {
                            const img = e.target.previousElementSibling;
                            e.target.style.display = 'none';
                            img.style.display = 'block';
                          }}
                        >
                          <source src={asset.file.url} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      )}
                      
                      {asset.documents && asset.documents.length > 0 && asset.documents.map(doc => (
                        <p key={doc._id}>
                          <a href={doc.url} target="_blank" rel="noopener noreferrer">
                            <i className="fas fa-file-pdf"></i> {assetFileDisplayName(doc)}
                          </a>
                        </p>
                      ))}
                      
                      <div className="text-center align-items-center">
                        <Button 
                          variant="primary" 
                          size="sm" 
                          className="mr-2"
                          onClick={() => navigate(`/edit-asset/${asset._id}`)}
                        >
                          Edit Asset
                        </Button>
                        <Button 
                          variant="danger" 
                          size="sm" 
                          className="mr-2"
                          onClick={() => handleRemoveAsset(plan._id, asset._id)}
                        >
                          Remove Asset
                        </Button>
                        <Button 
                          variant="danger" 
                          size="sm"
                          onClick={() => confirmDeleteAsset(asset._id)}
                        >
                          Delete Asset
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  {plan.type !== 'workshop' && (
                    <Form onSubmit={(e) => { e.preventDefault(); handleAddAssets(plan._id); }}>
                      <div style={{ display: 'flex', alignItems: 'end' }}>
                        <Form.Select 
                          multiple 
                          className="mr-2"
                          onChange={(e) => handleAssetSelection(plan._id, e)}
                          style={{ flex: 1 }}
                        >
                          {assets.map(asset => (
                            <option key={asset._id} value={asset._id}>
                              {asset.name}
                            </option>
                          ))}
                        </Form.Select>
                        <Button 
                          type="submit" 
                          variant="primary"
                          size="sm"
                          className="add-assets-btn"
                          disabled={!selectedAssets[plan._id]?.length}
                        >
                          Add Assets to Plan
                        </Button>
                      </div>
                    </Form>
                  )}
                  
                  <div className="d-flex justify-content-between">
                    <Button 
                      variant="primary" 
                      className="mr-2"
                      onClick={() => navigate(`/edit-plan/${plan._id}`)}
                    >
                      Edit Plan
                    </Button>
                    <Button 
                      variant="danger"
                      onClick={() => handleDeletePlan(plan._id, plan.name)}
                    >
                      Delete Plan
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
            <div className="mb-3"></div>
          </Col>
        ))}
      </Row>

      {/* Create Plan Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Plan Creation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCreatePlan}>
            <Form.Group className="mb-3">
              <Form.Label>Plan Name:</Form.Label>
              <Form.Control
                type="text"
                name="name"
                placeholder="Plan Name"
                value={createPlanData.name}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Duration:</Form.Label>
              <Form.Control
                type="number"
                step="0.1"
                name="duration"
                placeholder="Plan Duration in Days"
                value={createPlanData.duration}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Price:</Form.Label>
              <Form.Control
                type="number"
                name="price"
                placeholder="Plan Price"
                value={createPlanData.price}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Plan Description:</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                placeholder="Plan Description"
                value={createPlanData.description}
                onChange={handleInputChange}
                className="preserve-whitespace"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Plan Type:</Form.Label>
              <Form.Select
                name="type"
                value={createPlanData.type}
                onChange={handleInputChange}
                required
              >
                <option value="subscription">Subscription</option>
                <option value="workshop">Workshop</option>
                <option value="other">Other</option>
              </Form.Select>
            </Form.Group>
            {createPlanData.type === 'workshop' && (
              <Form.Group className="mb-3">
                <Form.Label>Workshop Date:</Form.Label>
                <Form.Control
                  type="date"
                  name="workshopDate"
                  value={createPlanData.workshopDate}
                  onChange={handleInputChange}
                />
              </Form.Group>
            )}
            <Button type="submit" variant="primary">Create</Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="modal-overlay">
          <div className="delete-confirmation-modal">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the plan:</p>
              <p className="plan-title">"{deleteModal.planName}"</p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={cancelDeletePlan}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-danger"
                onClick={confirmDeletePlan}
              >
                Delete Plan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllPlans; 